const STORE_DOMAIN = (window.__SHOP__ && window.__SHOP__.permanentDomain) || window.location.hostname;
const STOREFRONT_TOKEN = window.__SF_TOKEN__;
const SF_ENDPOINT = `https://${STORE_DOMAIN}/api/2025-10/graphql.json`;
const marketCountryCode = (window.__SHOP__?.country || "").toUpperCase() || null;
const marketLanguageCode = (
  (window.__SHOP__?.locale || document.documentElement.lang || "en").split("-")[0] || "en"
).toUpperCase();

const locale = (window.__SHOP__ && window.__SHOP__.locale) || document.documentElement.lang || "en";
const defaultCurrency =
  (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
  (window.__SHOP__ && window.__SHOP__.currency) ||
  "USD";

const formatNumberForMoneyTemplate = (rawAmount, fractionDigits, thousandsSeparator, decimalSeparator) => {
  const numericAmount = Number.isFinite(+rawAmount) ? +rawAmount : 0;
  const fixedAmountString = numericAmount.toFixed(fractionDigits);
  const [integerPart, fractionalPart = ""] = fixedAmountString.split(".");
  const integerWithSeparators = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
  return fractionDigits > 0 ? `${integerWithSeparators}${decimalSeparator}${fractionalPart}` : integerWithSeparators;
};

const formatMoneyLikeLiquid = (rawAmount, { templateString, isoCurrencyCode } = {}) => {
  const resolvedTemplate = templateString || window.__SHOP__?.money_format || "{{amount}} {{currency}}";

  const tokenReplacements = {
    "{{amount}}": formatNumberForMoneyTemplate(rawAmount, 2, ",", "."),
    "{{amount_no_decimals}}": formatNumberForMoneyTemplate(rawAmount, 0, ",", "."),
    "{{amount_with_comma_separator}}": formatNumberForMoneyTemplate(rawAmount, 2, ".", ","),
    "{{amount_no_decimals_with_comma_separator}}": formatNumberForMoneyTemplate(rawAmount, 0, ".", ","),
  };

  let output = resolvedTemplate;
  for (const tokenKey in tokenReplacements) {
    if (output.includes(tokenKey)) {
      output = output.replace(tokenKey, tokenReplacements[tokenKey]);
    }
  }
  if (output.includes("{{currency}}")) {
    output = output.replace("{{currency}}", isoCurrencyCode || window.__SHOP__?.currency || "");
  }
  return output;
};

const formatMoneyV2LikeLiquid = (money, { withCurrency = false } = {}) => {
  if (!money) return "";
  const templateString = withCurrency ? window.__SHOP__?.money_with_currency_format : window.__SHOP__?.money_format;

  return formatMoneyLikeLiquid(money.amount, {
    templateString,
    isoCurrencyCode: money.currencyCode,
  });
};

const money = (amount, currencyCode) => {
  const amountNumber = Number.parseFloat(amount || 0);
  // Arabic prices keep Western digits and the currency code, like the cart drawer.
  const isArabic = String(locale).split("-")[0].toLowerCase() === "ar";
  try {
    return new Intl.NumberFormat(isArabic ? `${locale}-u-nu-latn` : locale, {
      style: "currency",
      currency: currencyCode || defaultCurrency,
      ...(isArabic ? { currencyDisplay: "code" } : {}),
    }).format(amountNumber);
  } catch {
    return `${amountNumber.toFixed(2)} ${currencyCode || defaultCurrency}`;
  }
};

const toLegacyId = (globalId) => {
  if (globalId == null) return "";

  const gidString = String(globalId).trim();
  if (gidString === "") return "";

  if (/^\d+$/.test(gidString)) return gidString;

  const segmentRegex = /\/(\d+)(?:\/|$)/g;
  let match;
  let lastCapturedId = null;

  while ((match = segmentRegex.exec(gidString)) !== null) {
    lastCapturedId = match[1];
  }

  if (lastCapturedId) return lastCapturedId;

  console.warn("[bundle] toLegacyId: expected '/<digits>' segment in gid; returning original", {
    input: gidString,
  });

  return gidString;
};

const getMetafield = (metafields, key) => {
  if (!Array.isArray(metafields)) return null;
  const metafield = metafields.find((item) => item && item.key === key);
  return metafield ? metafield.value : null;
};

const parseBoolean = (value) => {
  if (value === true || value === "true" || value === 1 || value === "1" || value === "yes") return true;
  if (value === false || value === "false" || value === 0 || value === "0" || value === "no") return false;
  return null;
};

const getStoreBooleanMetafield = (metafields, consolidatedKey, legacyKey) => {
  const consolidatedValue = getMetafield(metafields, consolidatedKey);
  if (consolidatedValue != null && consolidatedValue !== "") {
    try {
      const storeMap = typeof consolidatedValue === "string" ? JSON.parse(consolidatedValue) : consolidatedValue;
      const rawStore = String(window.__SHOP__?.store || "").trim().toLowerCase();
      const store = rawStore === "global" ? "uae" : rawStore;
      const matchingKey = Object.keys(storeMap || {}).find((key) => key.toLowerCase() === store);
      if (matchingKey) {
        const resolved = parseBoolean(storeMap[matchingKey]);
        if (resolved != null) return resolved;
      }
    } catch (error) {
      console.warn(`[Bundle] Invalid ${consolidatedKey} JSON`, error);
    }
  }

  return parseBoolean(getMetafield(metafields, legacyKey));
};

const getMetafieldList = (metafields, key) => {
  const value = getMetafield(metafields, key);
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const applyEarlyAccess = (enableWaitlist, available, metafields) => {
  if (!enableWaitlist || !available) return enableWaitlist;

  const enableEarlyAccess = getStoreBooleanMetafield(metafields, "enable_early_access", null);
  if (!enableEarlyAccess) return enableWaitlist;

  const eligibleEntries = getMetafieldList(metafields, "early_access_eligible_customers");
  // Theme setting: early access opens to every signed-in customer (snippets/effective-waitlist.liquid).
  if (window.__SHOP__?.earlyAccessSignedIn === true && eligibleEntries.length) return false;

  const customerTier = String(window.__SHOP__?.customerTier || "").trim().toLowerCase();
  const customerTags = (window.__SHOP__?.customerTags || []).map((tag) => String(tag).trim().toLowerCase());
  const isEligible = eligibleEntries.some((entry) => {
    const normalizedEntry = String(entry || "").trim().toLowerCase();
    if (!normalizedEntry) return false;
    if (normalizedEntry === customerTier) return true;
    return normalizedEntry.startsWith("tag:") && customerTags.includes(normalizedEntry.slice(4).trim());
  });

  return isEligible ? false : enableWaitlist;
};

const getWaitlistCopyMode = (tags = []) => {
  if (tags.includes("notify-signup-mousse")) return "signup-mousse";
  if (tags.includes("notify-signup")) return "signup";
  return "default";
};

const getMetafieldImageUrl = (metafields, key, productFallbackUrl) => {
  if (!Array.isArray(metafields)) return productFallbackUrl || null;
  const metafield = metafields.find((item) => item && item.key === key);
  if (!metafield) return productFallbackUrl || null;
  if (metafield.reference) {
    if (metafield.reference?.image && metafield.reference?.image.url) return metafield.reference.image.url;
    if (metafield.reference.url) return metafield.reference.url;
  }
  if (metafield?.references?.nodes?.length) {
    const node = metafield.references.nodes[0];
    if (node.image && node.image.url) return node.image.url;
    if (node.url) return node.url;
  }
  try {
    if (metafield?.value && /^https?:/.test(metafield.value)) return metafield.value;
  } catch {}
  return productFallbackUrl || null;
};

async function gql(query, variables) {
  const response = await fetch(SF_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!response.ok) throw new Error(`Storefront API HTTP ${response.status}`);
  const jsonResponse = await response.json();
  if (jsonResponse.errors) {
    console.error(jsonResponse.errors);
    throw new Error("Storefront API GraphQL errors");
  }
  return jsonResponse.data;
}

async function fetchAllMainProductVariantsByHandle(handle) {
  const query = `
    query ProductVariants(
      $handle: String!,
      $after: String,
      $country: CountryCode,
      $language: LanguageCode
    ) @inContext(country: $country, language: $language) {
      product(handle: $handle) {
        id
        variants(first: 250, after: $after) {
          edges {
            cursor
            node {
              id
              title
              sku
              availableForSale
              quantityAvailable
              selectedOptions { name value }
              price { amount currencyCode }
              compareAtPrice { amount currencyCode }
              metafields(identifiers: [
                {namespace: "custom", key: "bundled_products"},
                {namespace: "custom", key: "c__maxOrderQuantity"},
                {namespace: "custom", key: "c_enablewaitlist"},
                {namespace: "custom", key: "enable_waitlist_consolidated_json"},
                {namespace: "custom", key: "back_in_stock_disabled"},
                {namespace: "custom", key: "back_in_stock_disabled_consolidated_json"},
                {namespace: "custom", key: "c__waitlistListID"},
                {namespace: "custom", key: "enable_early_access"},
                {namespace: "custom", key: "early_access_eligible_customers"},
                {namespace: "custom", key: "product_loyalty_flag"}
              ]) { key type value }
            }
          }
          pageInfo { hasNextPage }
        }
      }
    }
  `;

  let hasNext = true;
  let after = null;
  const allVariants = [];
  let productId = null;

  while (hasNext) {
    const responseData = await gql(query, {
      handle,
      after,
      country: marketCountryCode,
      language: marketLanguageCode,
    });
    const productNode = responseData.product;
    if (!productNode) break;
    productId = productNode.id;
    const edges = productNode.variants.edges || [];
    for (const edge of edges) allVariants.push(edge.node);
    hasNext = productNode.variants.pageInfo.hasNextPage;
    after = hasNext ? edges[edges.length - 1].cursor : null;
  }

  return { productId, variants: allVariants };
}

async function fetchProductsByHandles(handles = []) {
  if (!handles.length) return {};
  const chunks = [];
  const batchSize = 18;

  for (let index = 0; index < handles.length; index += batchSize) {
    chunks.push(handles.slice(index, index + batchSize));
  }

  const byHandle = {};

  for (const group of chunks) {
    const fields = group
      .map(
        (handleInGroup, index) => `
      p${index}: product(handle: ${JSON.stringify(handleInGroup)}) {
        handle
        tags
        featuredImage { url altText }
        variants(first: 250) {
          nodes {
            id
            sku
            availableForSale
            quantityAvailable
            selectedOptions { name value }
            price { amount currencyCode }
            compareAtPrice { amount currencyCode }
            metafields(identifiers: [
              {namespace:"custom", key:"images"},
              {namespace:"custom", key:"shortDescription"},
              {namespace:"custom", key:"c__sizeLabel__default"},
              {namespace:"custom", key:"c__isHudaShade"},
              {namespace:"custom", key:"c_enablewaitlist"},
              {namespace:"custom", key:"enable_waitlist_consolidated_json"},
              {namespace:"custom", key:"back_in_stock_disabled"},
              {namespace:"custom", key:"back_in_stock_disabled_consolidated_json"},
              {namespace:"custom", key:"c__waitlistListID"},
              {namespace:"custom", key:"enable_early_access"},
              {namespace:"custom", key:"early_access_eligible_customers"}
            ]) {
              key
              type
              value
              reference {
                ... on MediaImage { image { url altText } }
                ... on GenericFile { url }
              }
              references(first: 10) {
                nodes {
                  ... on MediaImage { image { url altText } }
                  ... on GenericFile { url }
                }
              }
            }
          }
        }
      }`,
      )
      .join("\n");

    const productsQuery = `
      query ProductsByHandles(
        $country: CountryCode,
        $language: LanguageCode
      ) @inContext(country: $country, language: $language) {
        ${fields}
      }
    `;

    const responseData = await gql(productsQuery, {
      country: marketCountryCode,
      language: marketLanguageCode,
    });

    Object.values(responseData).forEach((product) => {
      if (!product) return;
      byHandle[product.handle] = product;
    });
  }

  return byHandle;
}

// Checkout offer (snippets/checkout-offer-percentage.liquid): the price after the offer, with the
// current price as the "was" price, as the cart shows it. A real compare-at price wins.
const applyCheckoutOffer = (price, compareAtPrice, percentage) => {
  const amount = Number(price?.amount || 0);
  const offerPercentage = Number(percentage) || 0;
  if (offerPercentage <= 0 || offerPercentage >= 100 || amount <= 0) return { price, compareAtPrice };
  if (compareAtPrice?.amount && Number(compareAtPrice.amount) > amount) return { price, compareAtPrice };
  const cents = Math.round(amount * 100);
  const discount = Math.round((cents * offerPercentage) / 100);
  return { price: { ...price, amount: ((cents - discount) / 100).toFixed(2) }, compareAtPrice: price };
};

// The Storefront API answers in English while Arabic is unpublished, so an Arabic page swaps an
// English component description for the one Liquid renders from the store's Arabic translation
// (sections/arabic-bundle-copy.liquid, in the component product's context). Each product is
// fetched once, and only when English comes back; once Arabic is published this never runs.
const isArabicPage = document.documentElement.lang.toLowerCase().startsWith("ar");
const arabicBundleCopyByHandle = new Map();
const fetchArabicBundleCopy = (handle) => {
  if (!arabicBundleCopyByHandle.has(handle)) {
    const root = window.Shopify?.routes?.root || "/";
    const request = fetch(`${root}products/${encodeURIComponent(handle)}?sections=arabic-bundle-copy`)
      .then((response) => (response.ok ? response.json() : {}))
      .then((sections) => {
        // sections= answers {"arabic-bundle-copy": "<div ...><script type=\"application/json\">{...}</script></div>"}.
        const html = sections?.["arabic-bundle-copy"] || "";
        const sectionDocument = new DOMParser().parseFromString(html, "text/html");
        const dataScript = sectionDocument.querySelector('script[type="application/json"]');
        return dataScript ? JSON.parse(dataScript.textContent) : {};
      })
      .catch(() => ({}));
    arabicBundleCopyByHandle.set(handle, request);
  }
  return arabicBundleCopyByHandle.get(handle);
};
const localizeShortDescription = async (handle, variantId, description) => {
  if (!isArabicPage || typeof description !== "string" || !description.trim() || /[\u0600-\u06ff]/.test(description)) {
    return description;
  }
  const arabicCopy = (await fetchArabicBundleCopy(handle))?.[variantId];
  return typeof arabicCopy === "string" && arabicCopy.trim() ? arabicCopy : description;
};

const __bundleCache = new Map();

async function buildParsedBundleData(productHandle, { checkoutPercentage = 0 } = {}) {
  const cacheKey = `${productHandle}:${Number(checkoutPercentage) || 0}`;
  if (__bundleCache.has(cacheKey)) return __bundleCache.get(cacheKey);

  const { productId, variants: mainVariants } = await fetchAllMainProductVariantsByHandle(productHandle);

  const pairSet = new Set();
  const mainBundleVariants = [];
  const bundledByVariantId = new Map();
  const parentLoyaltyByVariantId = new Map();

  for (const variantNode of mainVariants) {
    const maxQty = getMetafield(variantNode.metafields, "c__maxOrderQuantity");
    const bundled = (getMetafield(variantNode.metafields, "bundled_products") || "").trim();
    const enableWaitlist = applyEarlyAccess(
      getStoreBooleanMetafield(
        variantNode.metafields,
        "enable_waitlist_consolidated_json",
        "c_enablewaitlist",
      ),
      !!variantNode.availableForSale,
      variantNode.metafields,
    );
    const variantId = toLegacyId(variantNode.id);
    bundledByVariantId.set(variantId, bundled);
    parentLoyaltyByVariantId.set(
      variantId,
      parseBoolean(getMetafield(variantNode.metafields, "product_loyalty_flag")) === true,
    );

    const { price: offerPrice, compareAtPrice: offerCompareAtPrice } = applyCheckoutOffer(
      variantNode.price,
      variantNode.compareAtPrice,
      checkoutPercentage,
    );

    mainBundleVariants.push({
      productId: toLegacyId(productId),
      id: toLegacyId(variantNode.id),
      sku: variantNode.sku,
      available: !!variantNode.availableForSale,
      inventory: typeof variantNode.quantityAvailable === "number" ? variantNode.quantityAvailable : null,
      max_product_quantity: maxQty ? Number(maxQty) : null,
      price: formatMoneyV2LikeLiquid(offerPrice),
      compare_at_price: offerCompareAtPrice ? formatMoneyV2LikeLiquid(offerCompareAtPrice) : "",
      save_price:
        offerCompareAtPrice?.amount &&
        Number(offerCompareAtPrice.amount) > Number(offerPrice?.amount || 0)
          ? formatMoneyLikeLiquid(Number(offerCompareAtPrice.amount) - Number(offerPrice.amount), {
              template: window.__SHOP__?.money_format,
              currencyCode: offerPrice?.currencyCode,
            })
          : "",
      options: (variantNode.selectedOptions || []).map((option) => (option ? String(option.value).trim() : "")),
      metafields: {
        custom: {
          enableWaitlist,
          backInStockDisabled: getStoreBooleanMetafield(
            variantNode.metafields,
            "back_in_stock_disabled_consolidated_json",
            "back_in_stock_disabled",
          ),
          waitListID: getMetafield(variantNode.metafields, "c__waitlistListID"),
        },
      },
    });

    if (bundled) {
      const parts = bundled
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
      for (const pair of parts) {
        const [handleString, skuString] = pair.split(":").map((value) => (value || "").trim());
        if (handleString && skuString) pairSet.add(`${handleString}::${skuString}`);
      }
    }
  }

  const uniqueHandles = Array.from(pairSet).map((pairKey) => pairKey.split("::")[0]);
  const handles = Array.from(new Set(uniqueHandles));

  const productsByHandle = await fetchProductsByHandles(handles);

  const componentVariants = [];
  const seenVariantIds = new Set();

  for (const pairKey of pairSet) {
    const [handleString, sku] = pairKey.split("::");
    const product = productsByHandle[handleString];
    if (!product) continue;

    const variantNodes = product.variants?.nodes || [];
    const match = variantNodes.find((variantNode) => variantNode.sku && String(variantNode.sku).trim() === sku);

    if (!match) continue;

    const imageUrl =
      getMetafieldImageUrl(match.metafields, "images", product.featuredImage?.url) ||
      product.featuredImage?.url ||
      null;

    const priceStr = money(match.price?.amount, match.price?.currencyCode);
    const compareStr = match.compareAtPrice?.amount
      ? money(match.compareAtPrice.amount, match.compareAtPrice.currencyCode)
      : "";
    const saveStr =
      match.compareAtPrice?.amount && Number(match.compareAtPrice.amount) > Number(match.price?.amount || 0)
        ? money(Number(match.compareAtPrice.amount) - Number(match.price.amount), match.price.currencyCode)
        : "";

    const idLegacy = toLegacyId(match.id);
    if (seenVariantIds.has(idLegacy)) continue;
    seenVariantIds.add(idLegacy);

    const enableWaitlist = applyEarlyAccess(
      getStoreBooleanMetafield(match.metafields, "enable_waitlist_consolidated_json", "c_enablewaitlist"),
      !!match.availableForSale,
      match.metafields,
    );

    componentVariants.push({
      id: idLegacy,
      sku: match.sku,
      available: !!match.availableForSale,
      inventory: typeof match.quantityAvailable === "number" ? match.quantityAvailable : null,
      price: priceStr,
      compare_at_price: compareStr,
      save_price: saveStr,
      options: (match.selectedOptions || []).map((option) => (option ? String(option.value).trim() : "")),
      metafields: {
        custom: {
          image_url: imageUrl,
          shortDescription: localizeShortDescription(
            handleString,
            idLegacy,
            getMetafield(match.metafields, "shortDescription"),
          ),
          size: getMetafield(match.metafields, "c__sizeLabel__default"),
          isHudaShade: getMetafield(match.metafields, "c__isHudaShade"),
          enableWaitlist,
          backInStockDisabled: getStoreBooleanMetafield(
            match.metafields,
            "back_in_stock_disabled_consolidated_json",
            "back_in_stock_disabled",
          ),
          waitListID: getMetafield(match.metafields, "c__waitlistListID"),
          waitlistCopyMode: getWaitlistCopyMode(product.tags),
        },
      },
    });
  }

  // Resolved together, so each component product's Arabic copy loads in parallel.
  await Promise.all(
    componentVariants.map(async ({ metafields: { custom } }) => {
      custom.shortDescription = await custom.shortDescription;
    }),
  );

  mainBundleVariants.forEach((bundleVariant) => {
    bundleVariant.metafields.custom.loyaltyGate = parentLoyaltyByVariantId.get(bundleVariant.id) === true;
  });

  const parsed = {
    bundle_variants: mainBundleVariants,
    variants: componentVariants,
  };

  __bundleCache.set(cacheKey, parsed);
  return parsed;
}

if (!window.buildParsedBundleData) {
  window.buildParsedBundleData = buildParsedBundleData;
}
